import { useRef, useState } from "react";
import { useScrollReveal } from "../../../shared/components/ScrollReveal/useScrollReveal";
import PageHero from "../../../shared/components/sections/PageHero/PageHero";
import SectionHeading from "../../../shared/components/ui/SectionHeading/SectionHeading";
import { aboutPageData } from "./data/aboutPageData";
import "../home/homeBase.css";
import "./aboutPage.css";

const TAB_KEYS = Object.keys(aboutPageData.tabs);

function renderTitleLines(lines = []) {
  return lines.map((line, index) => (
    <span key={`${line}-${index}`}>
      {line}
      {index < lines.length - 1 && <br />}
    </span>
  ));
}

export default function AboutPage() {
  const tabButtonRefs = useRef({});

  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window === "undefined") return "sobre";

    const savedTab = localStorage.getItem("about-active-tab");
    return savedTab && aboutPageData.tabs[savedTab] ? savedTab : "sobre";
  });

  const currentTab = aboutPageData.tabs[activeTab];

  useScrollReveal(".reveal-on-scroll", {
    triggerRatio: 0.84,
    once: false,
  });

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);

    if (typeof window !== "undefined") {
      localStorage.setItem("about-active-tab", tabKey);
    }
  };

  const focusTabByIndex = (nextIndex) => {
    const safeIndex = (nextIndex + TAB_KEYS.length) % TAB_KEYS.length;
    const nextTabKey = TAB_KEYS[safeIndex];

    handleTabChange(nextTabKey);
    tabButtonRefs.current[nextTabKey]?.focus();
  };

  const handleTabKeyDown = (event, currentIndex) => {
    const keyActions = {
      ArrowRight: () => focusTabByIndex(currentIndex + 1),
      ArrowDown: () => focusTabByIndex(currentIndex + 1),
      ArrowLeft: () => focusTabByIndex(currentIndex - 1),
      ArrowUp: () => focusTabByIndex(currentIndex - 1),
      Home: () => focusTabByIndex(0),
      End: () => focusTabByIndex(TAB_KEYS.length - 1),
    };

    const action = keyActions[event.key];

    if (!action) return;

    event.preventDefault();
    action();
  };

  return (
    <main className="about-page-v4">
      <PageHero
        content={aboutPageData.hero}
        className="about-page__hero"
        title={renderTitleLines(aboutPageData.hero.titleLines)}
        primaryCta={aboutPageData.hero.primaryCta}
        secondaryCta={aboutPageData.hero.secondaryCta}
      />

      <section className="about-tabs" aria-labelledby="about-tabs-title">
        <div className="container">
          <div className="about-tabs__intro reveal-on-scroll reveal-on-scroll--up">
            <span className="about-tabs__intro-eyebrow">
              Conheça a profissional
            </span>

            <h2 id="about-tabs-title">
              Uma trajetória construída com cuidado, propósito e escuta.
            </h2>

            <p>
              Conheça um pouco mais sobre a história, a forma de trabalho e os valores que guiam cada atendimento.
            </p>
          </div>

          <div className="about-tabs__nav-wrap reveal-on-scroll reveal-on-scroll--up">
            <div
              className="about-tabs__nav"
              role="tablist"
              aria-label="Seções sobre a Dionara"
            >
              {Object.entries(aboutPageData.tabs).map(([key, tab], index) => (
                <button
                  key={key}
                  ref={(element) => {
                    tabButtonRefs.current[key] = element;
                  }}
                  id={`about-tab-${key}`}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === key}
                  aria-controls={`about-panel-${key}`}
                  tabIndex={activeTab === key ? 0 : -1}
                  className={activeTab === key ? "active" : ""}
                  onClick={() => handleTabChange(key)}
                  onKeyDown={(event) => handleTabKeyDown(event, index)}
                >
                  <span className="about-tabs__tab-text">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div
            id={`about-panel-${activeTab}`}
            className="about-tabs__content about-tabs__content--animated"
            role="tabpanel"
            aria-labelledby={`about-tab-${activeTab}`}
            tabIndex={0}
            key={activeTab}
          >
            <SectionHeading
              eyebrow={currentTab.eyebrow}
              title={currentTab.title}
              description={currentTab.lead}
              align="center"
              className="about-tabs__header"
              descriptionClassName="about-tabs__lead"
            />

            <div className="about-tabs__body">
              <div className="about-tabs__text">
                {currentTab.paragraphs?.map((paragraph, index) => (
                  <p
                    key={`${activeTab}-paragraph-${index}`}
                    className="about-tabs__paragraph"
                    style={{
                      animationDelay: `${0.08 + index * 0.08}s`,
                    }}
                  >
                    {paragraph}
                  </p>
                ))}

                {currentTab.items && (
                  <div className="about-tabs__info-list">
                    {currentTab.items.map((item, index) => (
                      <article
                        className="about-tabs__info-item"
                        key={`${activeTab}-item-${index}`}
                        style={{
                          animationDelay: `${0.14 + index * 0.08}s`,
                        }}
                      >
                        <span className="about-tabs__info-number">
                          {String(index + 1).padStart(2, "0")}
                        </span>

                        <h3>{item.title}</h3>
                        <p>{item.text}</p>
                      </article>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="about-page-v4__quote" aria-label="Mensagem da Dionara">
        <div className="container">
          <div className="about-page-v4__quote-box reveal-on-scroll reveal-on-scroll--scale">
            <div className="about-page-v4__quote-content">
              <p>
                <span className="about-page-v4__quote-inline">“</span>
                {aboutPageData.quote.text}
                <span className="about-page-v4__quote-inline">”</span>
              </p>

              <span className="about-page-v4__quote-author">
                {aboutPageData.quote.author}
              </span>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}